/** Seed jobs (§12.7): demo B-1001, pepper B-1002, hatfield B-1003 (live). */
import { createRecord, getDb, getSettings, putSettingsRaw, snapshotNow, defaultSettings } from '../core/db';
import { nowIso } from '../core/ids';
import type { Job, Provenance, Verification } from '../core/types';
import { PRICE_BOOK_SEED } from './pricebook';
import { DEFAULT_TERMS_TEXT } from './terms';

const prov = (source: Provenance['source'] = 'import'): Provenance =>
  ({ source, setBy: 'system', setAt: nowIso(), detail: 'seed' });
const unverified: Verification = { verified: false };

export async function seedIfEmpty(): Promise<boolean> {
  const db = await getDb();
  if ((await db.count('jobs')) > 0) return false;

  const settings = { ...defaultSettings(), termsText: DEFAULT_TERMS_TEXT };
  await putSettingsRaw(settings);

  for (const p of PRICE_BOOK_SEED) {
    await createRecord('priceBook', { ...p, retired: false }, { snapshot: false });
  }

  const mkJob = (j: Omit<Job, keyof import('../core/types').Envelope>) =>
    createRecord('jobs', j, { snapshot: false });

  // Sample clients. The seeds are the first thing anyone sees, so they should
  // model the shape the app actually wants: a proposal is addressed to an
  // association record, not to a line of text in a notes field.
  const pepperClient = await createRecord('clients', {
    name: 'Ellen Marsh', company: 'Pepper Ridge Homeowners Association',
    phone: '602-555-0118', email: 'board@pepperridgehoa.example',
    address: 'PO Box 41220, Phoenix, AZ 85080',
    notes: 'Sample client. Board meets the second Tuesday.',
  }, { snapshot: false });
  const demoClient = await createRecord('clients', {
    name: 'Ray Delgado', company: '', phone: '480-555-0143',
    notes: 'Sample client — residential demo.',
  }, { snapshot: false });

  // --- demo (B-1001) ---
  const demo = await mkJob({
    clientId: demoClient.id,
    title: 'Front yard xeriscape — demo',
    siteAddress: '4225 E Shea Blvd, Phoenix, AZ 85028',
    lat: 33.5824, lng: -111.9899, zoom: 19,
    status: 'bid_sent', bidNumber: 'B-1001',
    notes: 'Demo Customer · (602) 555-0148 · demo@example.com\nConvert front lawn to desert landscape. Remove existing grass, cap two sprinkler heads, keep the mesquite by the driveway. This is a demo job — open Documents to see the output, then delete me.',
    fin: { markupPct: 0, taxPct: 0, depositPct: 25, adjustCents: 0 },
    parcel: null,
  });
  const demoLines: [string, number, string][] = [
    ['dg', 1850, 'Front yard granite'], ['turfart', 420, 'Play strip'],
    ['edge', 140, 'Border edging'], ['drip', 220, 'Drip to new plants'],
    ['tree15', 3, 'Palo verde trees'], ['shrub', 12, 'Lantana + sage'],
  ];
  for (const [priceItemId, qty, label] of demoLines) {
    await createRecord('takeoffLines', {
      jobId: demo.id, priceItemId, qty, label, provenance: prov(), verification: unverified,
    }, { snapshot: false });
  }
  const demoExtras: [string, string, number, number][] = [
    ['One-time yard cleanup', 'each', 1, 28500], ['Debris haul-off', 'load', 2, 15000],
  ];
  for (const [name, unit, qty, rateCents] of demoExtras) {
    await createRecord('takeoffLines', {
      jobId: demo.id, custom: { name, unit }, qty, label: name,
      unitPriceCentsOverride: rateCents, provenance: prov(), verification: unverified,
    }, { snapshot: false });
  }
  const demoInv: [string, string, number, string, string][] = [
    ['tree', 'Velvet mesquite', 1, 'good', 'Keep. Canopy over driveway, prune for clearance'],
    ['palm', 'Mexican fan palm', 2, 'fair', 'Skinning overdue'],
    ['shrub', 'Oleander (hedge)', 5, 'poor', 'Scale damage visible from street. Recommend removal'],
    ['valve', '', 2, 'unknown', 'Box lids visible east side'],
    ['drain', '', 1, 'unknown', 'Scupper at driveway apron'],
  ];
  for (const [cat, species, qty, cond, notes] of demoInv) {
    await createRecord('inventoryItems', {
      jobId: demo.id, cat: cat as never, species: species || undefined, qty,
      cond: cond as never, notes, photoIds: [], provenance: prov(), verification: unverified,
    }, { snapshot: false });
  }

  // --- pepper (B-1002) ---
  const pepper = await mkJob({
    clientId: pepperClient.id,
    title: 'Pepper Ridge HOA — commons (sample)',
    siteAddress: 'E Villa Theresa Dr & N 25th St, Phoenix, AZ 85032',
    lat: 33.654618, lng: -112.028014, zoom: 18,
    status: 'bid_sent', bidNumber: 'B-1002',
    notes: 'Pepper Ridge HOA (sample). Commercial sample: townhome HOA common areas. Annual maintenance contract plus fall overseed and granite refresh. Open Measure and click Scan area to pull real OpenStreetMap data for this community.',
    fin: { markupPct: 10, taxPct: 0, depositPct: 15, adjustCents: 0 },
    parcel: null,
  });
  const pepperLines: [string, number, string][] = [
    ['overseed', 24500, 'Common turf — fall overseed'],
    ['topdress', 8200, 'Entry + frontage DG refresh'],
    ['drip', 650, 'Replace drip runs, phase 1'],
    ['shrub', 40, 'Replacement shrubs'],
    ['tree15', 6, 'Replacement trees'],
  ];
  for (const [priceItemId, qty, label] of pepperLines) {
    await createRecord('takeoffLines', {
      jobId: pepper.id, priceItemId, qty, label, provenance: prov(), verification: unverified,
    }, { snapshot: false });
  }
  const pepperExtras: [string, string, number, number][] = [
    ['Commercial maintenance contract', 'month', 12, 85000],
    ['Backflow test (certified)', 'each', 2, 9500],
    ['Palm skinning / trim — entry palms', 'each', 8, 8500],
  ];
  for (const [name, unit, qty, rateCents] of pepperExtras) {
    await createRecord('takeoffLines', {
      jobId: pepper.id, custom: { name, unit }, qty, label: name,
      unitPriceCentsOverride: rateCents, provenance: prov(), verification: unverified,
    }, { snapshot: false });
  }
  const pepperInv: [string, string, number, string, string][] = [
    ['turf', 'Common bermuda', 24500, 'fair', 'Summer stress patches near mailbox clusters'],
    ['tree', 'Mesquite / palo verde mix', 38, 'fair', 'Approx. count from imagery; 6 show deadwood'],
    ['palm', 'Mexican fan palm', 8, 'fair', 'Entry corridor'],
    ['hedge', 'Oleander perimeter', 420, 'good', 'Along perimeter wall'],
    ['valve', '', 14, 'unknown', 'Controller count unknown'],
    ['drain', '', 4, 'unknown', 'Retention basin scuppers'],
  ];
  for (const [cat, species, qty, cond, notes] of pepperInv) {
    await createRecord('inventoryItems', {
      jobId: pepper.id, cat: cat as never, species: species || undefined, qty,
      cond: cond as never, notes, photoIds: [], provenance: prov(), verification: unverified,
    }, { snapshot: false });
  }

  // --- hatfield (B-1003, live, empty takeoff) ---
  await mkJob({
    title: '10408 W Hatfield Rd — south & east sides',
    siteAddress: '10408 W Hatfield Rd, Peoria, AZ 85383',
    lat: 33.70723, lng: -112.283583, zoom: 19,
    status: 'lead', bidNumber: 'B-1003',
    notes: 'Landscape improvement on the south and east sides of the property. Desk survey: Load parcel, Scan area, outline the south and east zones on the aerial, then print the walk-through sheet.',
    fin: { markupPct: 0, taxPct: 0, depositPct: 25, adjustCents: 0 },
    parcel: null,
  });

  await snapshotNow('manual');
  return true;
}

export { getSettings };
